// Defense-in-depth client-side SVG sanitizer. Mirrors the server-side
// SvgSanitizer (dorsia-api) so any divergence between the two is a regression.
// If a malicious SVG ever bypasses the server (CDN poisoning, future code path
// that writes without running through SanitizeVenueMapMedia), this is the last
// gate before `container.innerHTML = ...` in VenueMap.tsx.

const FORBIDDEN_ELEMENTS = new Set([
  'script',
  'foreignobject',
  'iframe',
  'object',
  'embed',
  'animate',
  'animatemotion',
  'animatetransform',
  'animatecolor',
  'set',
  'use',
]);

// <a> wraps shapes for navigation; drop the wrapper, keep children.
const UNWRAP_ELEMENTS = new Set(['a']);

const SAFE_IMAGE_DATA_URI_PATTERN = /^data:image\/(png|jpe?g|gif|webp);base64,/i;

export class InvalidSvgError extends Error {}

export function sanitizeSvg(svg: string): string {
  const doc = new DOMParser().parseFromString(svg, 'image/svg+xml');
  if (doc.querySelector('parsererror')) {
    throw new InvalidSvgError('SVG could not be parsed.');
  }
  const root = doc.documentElement;
  if (root.localName.toLowerCase() !== 'svg') {
    throw new InvalidSvgError('Root element must be <svg>.');
  }
  cleanElement(root);
  return new XMLSerializer().serializeToString(root);
}

function cleanElement(element: Element): void {
  const name = element.localName.toLowerCase();
  if (FORBIDDEN_ELEMENTS.has(name)) {
    element.remove();
    return;
  }
  if (UNWRAP_ELEMENTS.has(name)) {
    const parent = element.parentNode;
    if (parent) {
      const childNodes = Array.from(element.childNodes);
      for (const child of childNodes) {
        parent.insertBefore(child, element);
      }
      element.remove();
      // Children were unwrapped without sanitization — recurse so any
      // forbidden descendants (e.g. <a><script/></a>) still get stripped.
      for (const child of childNodes) {
        if (child instanceof Element) {
          cleanElement(child);
        }
      }
    }
    return;
  }
  for (const attr of Array.from(element.attributes)) {
    if (isDangerousAttribute(attr.name, attr.value)) {
      element.removeAttribute(attr.name);
    }
  }
  for (const child of Array.from(element.children)) {
    cleanElement(child);
  }
}

function isDangerousAttribute(name: string, value: string): boolean {
  const lower = name.toLowerCase();
  if (lower.startsWith('on')) {
    return true;
  }
  const localName = lower.includes(':') ? lower.split(':').pop()! : lower;
  if (localName === 'href') {
    return isDangerousUrl(value);
  }
  return false;
}

function isDangerousUrl(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  if (normalized.startsWith('javascript:') || normalized.startsWith('vbscript:')) {
    return true;
  }
  if (normalized.startsWith('data:')) {
    return !SAFE_IMAGE_DATA_URI_PATTERN.test(normalized);
  }
  return false;
}
